import React, {FC, Fragment} from "react";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import styled from "@emotion/styled";
import {NavLink} from "react-router-dom";
import {Box, Button, Container, useMediaQuery} from "@mui/material";
import {Bindings} from "data/constants/bindings";
import {MENU_LINKS} from "data/constants/navigation";
import {HEADER_HEIGHT, MOBILE_HEADER_HEIGHT, sponsorLink, sponsorLogo} from "data/constants/header";
import {Exist} from "views/components/exist/exist.component";
import {BurgerButton, PureButton, SponsorWrapper} from "views/components/style";
import {type IHeaderController} from "views/components/header/header.controller";
import {MobileNav} from "./mobile_nav/mobile_nav.component";
import {media} from "assets/css/media";
import {ReactComponent as IconAccount} from "assets/img/icons/icon-account.svg";
import nflLogoIcon from "assets/img/nfl_logo.svg";
import logoTextIcon from "assets/img/logoText.svg";
import {LoginBanner} from "./login_banner/login_banner.component";
import {LanguageSelect} from "./language_select/language_select.component";
import {SYNDICATE} from "data/constants";
import {SyndicatesType} from "data/enums";
import {ReactComponent as DeSponsorLogo} from "assets/img/sport_de_logo.svg";

const HeaderWrapper = styled(Box)`
	width: 100%;
	background: #193361;
	padding: 8px 0;
	height: ${MOBILE_HEADER_HEIGHT}px;
	display: flex;
	position: sticky;
	top: 0;
	left: 0;
	z-index: 50;

	@media (min-width: ${media.tablet}) {
		height: ${HEADER_HEIGHT}px;
	}
`;

const HeaderContainer = styled(Container)`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 20px;
	color: #ffffff;

	@media (min-width: ${media.tablet}) {
		max-width: 1248px;
	}
`;

const LeftPart = styled(Box)`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 10px;
	flex: 1 1 100%;

	@media (min-width: ${media.tablet}) {
		gap: 20px;
	}
`;

const RightPart = styled(Box)`
	position: relative;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 12px;
	flex: 1 1 100%;

	&.${SyndicatesType.US} {
		gap: 0;
	}

	button,
	a {
		min-width: unset;
		margin: 0;
	}

	@media (min-width: ${media.tablet}) {
		gap: 20px;

		&.${SyndicatesType.US} {
			gap: 20px;
		}
	}
`;

const LogoContainer = styled(NavLink)`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-right: 8px;
`;

const NflLogo = styled.img`
	height: 40px;

	@media (min-width: ${media.tablet}) {
		height: 48px;
	}
`;

const LogoText = styled.img`
	width: 70px;

	@media (min-width: ${media.tablet}) {
		width: 84px;
	}
`;

const DesktopMenuWrapper = styled(Box)`
	display: flex;
	gap: 8px;

	@media (min-width: ${media.tablet}) {
		gap: 20px;
	}
`;

const SponsorRow = styled(Box)`
	display: flex;
	align-items: center;
	color: #fff;
	gap: 8px;
	font-size: 12px;
	white-space: nowrap;
`;

const Link = styled(NavLink)`
	display: flex;
	align-items: center;
	color: #cecece;

	&.active,
	&:hover {
		color: #fff;

		path {
			fill: #fff;
		}
	}

	button {
		white-space: nowrap;
		font-size: 12px;
		min-width: 0;
		padding: 8px 4px;
		line-height: 140%;
		font-weight: 600;
		color: inherit;
		font-family: var(--fontFamilyBase);
	}

	path {
		fill: #cecece;
	}

	* {
		transition: all 0.3s;
	}

	@media (min-width: ${media.tablet}) {
		button {
			font-size: 14px;
		}
	}
`;

const SponsorIcon = styled.img`
	height: 32px;
	width: auto;

	@media (min-width: ${media.tablet}) {
		height: 40px;
	}

	&.${SyndicatesType.CA}, &.${SyndicatesType.DE} {
		height: 16px;

		@media (min-width: ${media.tablet}) {
			height: 20px;
		}
	}

	&.${SyndicatesType.US} {
		//height: 24px;
	}
`;

interface IHeader {
	isShowLoginAlert: boolean;
}

export const Header: React.FC<IHeader> = observer(({isShowLoginAlert}) => {
	const {i18n, isAuthorized, isOpenMenu, toggleMenu, SSOLogin} =
		useViewController<IHeaderController>(Bindings.HeaderController);
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);
	const openClass = isOpenMenu ? "open" : "";

	return (
		<Fragment>
			<Exist when={SYNDICATE === SyndicatesType.DE}>
				<DeHeader />
			</Exist>
			<HeaderWrapper>
				<HeaderContainer>
					<LeftPart>
						<LogoContainer to="/pick-by-seed">
							<NflLogo src={nflLogoIcon} alt="Nfl logo" />
							<LogoText src={logoTextIcon} alt="path to the playoffs" />
						</LogoContainer>

						<Exist when={!isMobile}>
							<DesktopMenuWrapper>
								{MENU_LINKS.map(({Icon, url, name}, index) => (
									<Link key={index} to={url}>
										<Button>{i18n.t(name)}</Button>

										{Icon && <Icon />}
									</Link>
								))}
							</DesktopMenuWrapper>
						</Exist>
					</LeftPart>

					<RightPart className={SYNDICATE}>
						<SponsorRow>
							{sponsorLogo && (
								<Fragment>
									<span>{i18n.t("header.nav.presented_by")}</span>
									<SponsorWrapper link={sponsorLink}>
										<SponsorIcon className={SYNDICATE} src={sponsorLogo} />
									</SponsorWrapper>
								</Fragment>
							)}
						</SponsorRow>

						<Exist when={!isMobile}>
							<Exist when={isAuthorized}>
								<NavLink to="/account">
									<IconAccount />
								</NavLink>
							</Exist>
							<Exist when={!isAuthorized}>
								<PureButton onClick={SSOLogin}>
									<IconAccount />
								</PureButton>
							</Exist>
						</Exist>

						<LanguageSelect />

						<Exist when={isMobile}>
							<BurgerButton onClick={toggleMenu} className={openClass} />
						</Exist>
					</RightPart>
				</HeaderContainer>

				<MobileNav
					isOpen={isOpenMenu}
					isAuthorized={isAuthorized}
					closeMenu={toggleMenu}
					SSOLoginHandler={SSOLogin}
				/>
			</HeaderWrapper>

			<Exist when={isShowLoginAlert && !isAuthorized}>
				<LoginBanner SSOLogin={SSOLogin} />
			</Exist>
		</Fragment>
	);
});

const SHeader = styled.div`
	align-items: center;
	background: #054850;
	box-sizing: border-box;
	color: #f3f3f3;
	display: flex;
	flex-flow: row;
	font: 15px/1.6 Titillium Web;
	height: 72px;
	justify-content: space-between;
	overflow: hidden;
	padding-bottom: 16px;
	padding-left: max(16px, 50vw - 1004px / 2);
	padding-right: max(16px, 50vw - 1004px / 2);
	padding-top: 16px;

	a {
		padding: 0 8px;
		color: inherit;
		display: inline-block;
		text-decoration: none;
		white-space: nowrap;
	}

	img {
		display: block;
	}

	nav {
		flex-grow: 1;
		order: 1;
		width: calc(50% - 16px);

		ul {
			display: flex;
			gap: 8px;
			list-style-type: none;
			margin: 0;
			min-width: 0;
			overflow-y: auto;
			padding: 0;

			li {
				display: list-item;
				text-align: -webkit-match-parent;
			}
		}

		@media (min-width: ${media.mobile}) {
			width: auto;
		}
	}
`;

const DeHeader: FC = () => {
	return (
		<SHeader>
			<a href="https://www.sport.de/" target="_blank" rel="noreferrer noopener">
				{/*<img*/}
				{/*	src="https://s.hs-data.com/comon/prj/isdc/v2/sportde/static/gfx/sport_de_logo.svg"*/}
				{/*	width="154"*/}
				{/*	height="30"*/}
				{/*	alt="sport.de"*/}
				{/*/>*/}
				<DeSponsorLogo width={154} height={30} />
			</a>

			<nav>
				<ul>
					<li>
						<a
							href="https://www.sport.de/heute-live/"
							target="_blank"
							rel="noreferrer noopener">
							Heute Live
						</a>
					</li>
					<li>
						<a
							href="https://www.sport.de/nfl/magazin/"
							target="_blank"
							rel="noreferrer noopener">
							NFL
						</a>
					</li>
					<li>
						<a
							href="https://www.sport.de/fussball/magazin/"
							target="_blank"
							rel="noreferrer noopener">
							Fußball
						</a>
					</li>
					<li>
						<a
							href="https://www.sport.de/motorsport/formel-1/magazin/"
							target="_blank"
							rel="noreferrer noopener">
							Formel 1
						</a>
					</li>
					<li>
						<a
							href="https://www.sport.de/wintersport/magazin/"
							target="_blank"
							rel="noreferrer noopener">
							Wintersport
						</a>
					</li>
					<li>
						<a
							href="https://www.sport.de/tennis/magazin/"
							target="_blank"
							rel="noreferrer noopener">
							Tennis
						</a>
					</li>
				</ul>
			</nav>
		</SHeader>
	);
};
